/**
 * PNG export through the browser's own renderer: render document -> SVG foreignObject -> canvas.
 * clip-path, ::before/::after, object-fit and transforms paint exactly as in the HTML download.
 */
import { documentDesignService } from '../../../services/documentDesignService';
import { blobToDataUrl } from './mediaCreatorExportCss';
import { isLogoImage } from './mediaCreatorDomRegistry';
import {
  buildArtboardCssWithStats,
  buildRenderBody,
  buildRenderDocument,
  hydrateMarkupInline,
  MC_FONT_STYLESHEET_URL,
} from './mediaCreatorRenderDocument';
import { collectForeignObjectDiagnostics, probeLayoutParity } from './mediaCreatorExportDiagnostics';

const KEEP_FONT_SUBSETS = new Set(['latin', 'latin-ext']);

/**
 * Pick @font-face blocks from a Google Fonts CSS2 response (subset comments precede each block).
 * @returns {string[]}
 */
export function selectFontFaceBlocks(cssText) {
  const text = String(cssText || '');
  const commented = [...text.matchAll(/\/\*\s*([\w-]+)\s*\*\/\s*(@font-face\s*\{[^}]*\})/g)];
  if (commented.length) {
    return commented.filter((m) => KEEP_FONT_SUBSETS.has(m[1])).map((m) => m[2]);
  }
  return [...text.matchAll(/@font-face\s*\{[^}]*\}/g)].map((m) => m[0]);
}

function fontFamiliesFromBlocks(blocks) {
  const fams = new Set();
  blocks.forEach((b) => {
    const m = b.match(/font-family\s*:\s*['"]?([^'";]+)['"]?/i);
    const w = b.match(/font-weight\s*:\s*(\d+)/i);
    if (m) fams.add(`${m[1].trim()} ${w ? w[1] : ''}`.trim());
  });
  return [...fams];
}

async function fetchAsDataUrl(url) {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return blobToDataUrl(await resp.blob());
}

async function embedFontCss() {
  const resp = await fetch(MC_FONT_STYLESHEET_URL);
  if (!resp.ok) throw new Error(`Font stylesheet HTTP ${resp.status}`);
  const blocks = selectFontFaceBlocks(await resp.text());
  const urlCache = new Map();
  const embedded = await Promise.all(
    blocks.map(async (block) => {
      const urls = [...block.matchAll(/url\(\s*['"]?([^'")]+)['"]?\s*\)/g)].map((m) => m[1]);
      let out = block;
      for (const url of urls) {
        if (!urlCache.has(url)) urlCache.set(url, fetchAsDataUrl(url));
        // eslint-disable-next-line no-await-in-loop
        const dataUrl = await urlCache.get(url);
        out = out.split(url).join(dataUrl);
      }
      return out;
    }),
  );
  return { css: embedded.join('\n'), families: fontFamiliesFromBlocks(blocks), error: null };
}

let fontCssPromise = null;

/** Embedded @font-face CSS (cached). Never throws; returns error text when fonts could not be fetched. */
export function loadEmbeddedFontCss() {
  if (!fontCssPromise) {
    fontCssPromise = embedFontCss().catch((err) => {
      fontCssPromise = null;
      return { css: '', families: [], error: err?.message || 'font fetch failed' };
    });
  }
  return fontCssPromise;
}

async function fetchAssetBlob(assetId, rendition, isLogo) {
  try {
    return await documentDesignService.fetchCompositionAssetBlob(assetId, rendition);
  } catch (err) {
    if (isLogo && rendition !== 'web') {
      return documentDesignService.fetchCompositionAssetBlob(assetId, 'web');
    }
    throw err;
  }
}

async function imageDataUrl(img) {
  const assetId = img.getAttribute('data-media-asset-id');
  const isLogo = isLogoImage(img);
  if (assetId) {
    const rendition = img.getAttribute('data-rendition') || (isLogo ? 'original' : 'web');
    const blob = await fetchAssetBlob(assetId, rendition, isLogo);
    return blobToDataUrl(blob);
  }
  const src = img.getAttribute('src') || '';
  const resp = await fetch(src, { credentials: 'same-origin' });
  if (!resp.ok) return null;
  return blobToDataUrl(await resp.blob());
}

/** Serialize CSS + host markup into an SVG with a foreignObject (XHTML). */
export function buildForeignObjectSvg({ css, fontCss = '', bodyHtml, w, h, scale = 1 }) {
  const doc = document.implementation.createHTMLDocument('');
  const wrap = doc.createElement('div');
  wrap.setAttribute(
    'style',
    `width:${w}px;height:${h}px;margin:0;padding:0;overflow:hidden;position:relative;`,
  );
  wrap.innerHTML = `<style>${fontCss}</style><style>.mc-html-host{margin:0;}\n${css}</style>${bodyHtml}`;
  const xhtml = new XMLSerializer().serializeToString(wrap);
  const outW = Math.round(w * scale);
  const outH = Math.round(h * scale);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${outW}" height="${outH}" viewBox="0 0 ${w} ${h}">`
    + `<foreignObject x="0" y="0" width="${w}" height="${h}">${xhtml}</foreignObject>`
    + '</svg>'
  );
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Browser could not render the export SVG'));
    img.src = url;
  });
}

const nextFrame = () => new Promise((r) => {
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => r());
  else setTimeout(r, 16);
});

async function rasterizeSvg(svg, w, h, scale, backgroundColor) {
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  const img = await loadImage(url);
  await img.decode?.().catch(() => undefined);
  await nextFrame();
  await nextFrame();
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(w * scale);
  canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  if (backgroundColor) {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/**
 * Render the composition to a canvas from the shared render document.
 * @returns {Promise<{ canvas: HTMLCanvasElement, diagnostics: object }>}
 */
export async function renderCompositionCanvas({
  layout,
  assetUrls,
  scale = 2,
  backgroundColor = null,
  liveArtboard = null,
}) {
  const w = layout?.size_px?.width || 1920;
  const h = layout?.size_px?.height || 1080;

  const [fonts, inline] = await Promise.all([
    loadEmbeddedFontCss(),
    hydrateMarkupInline(layout, { assetUrls, resolveDataUrl: imageDataUrl }),
  ]);
  if (inline.failures.length) {
    throw new Error(
      `Could not load image(s) for export (${inline.failures.join(', ')}) — sign in again or re-pick the photo.`,
    );
  }

  const { css, stats } = buildArtboardCssWithStats(layout);
  const svg = buildForeignObjectSvg({
    css,
    fontCss: fonts.css,
    bodyHtml: buildRenderBody(inline.innerHtml, layout),
    w,
    h,
    scale,
  });
  const canvas = await rasterizeSvg(svg, w, h, scale, backgroundColor);
  canvas.toDataURL('image/png');

  let parity = null;
  if (liveArtboard) {
    try {
      const probeDoc = buildRenderDocument(layout, {
        innerHtml: inline.innerHtml,
        fontCss: fonts.css,
      });
      parity = await probeLayoutParity(liveArtboard, probeDoc, layout);
    } catch (err) {
      parity = { error: err?.message || 'parity probe failed', rows: [] };
    }
  }

  const diagnostics = collectForeignObjectDiagnostics(layout, {
    scale,
    imagesInlined: inline.inlined,
    fontsEmbedded: fonts.families,
    fontError: fonts.error,
    cssStats: stats,
    svgBytes: svg.length,
    parity,
  });
  return { canvas, diagnostics };
}
